/**
 * Filters time-series values to only include data from startDate onwards
 * More efficient than using array.shift() in a loop (O(n) vs O(n²))
 *
 * @param {Array} values - Array of [timestamp, value] tuples
 * @param {number} startDate - Start date timestamp
 * @returns {Array} Filtered array starting from startDate
 */
export const filterByStartDate = (values, startDate) => {
	if (!values || values.length === 0) {
		return [[startDate, 0]];
	}
	const startIndex = values.findIndex(([timestamp]) => timestamp >= startDate);
	return startIndex >= 0 ? values.slice(startIndex) : values;
};

/**
 * Calculates deltas (differences) between consecutive values
 * Handles negative deltas by using current value instead of negative difference
 *
 * @param {Array} values - Array of [timestamp, value] tuples from API
 * @param {number} startDate - Start date timestamp for filtering
 * @param {number} initialValue - Initial value for first entry (default: 0)
 * @returns {Array} Array with delta values and filtered by start date
 */
export const calculateDeltas = (values, startDate, initialValue = 0) => {
	if (!values || values.length === 0) {
		return [];
	}

	// If empty array, return single entry with startDate and initialValue
	// if (values.length === 0) {
	// 	return [[startDate, initialValue]];
	// }

	const result = [[values[0][0], initialValue]];

	for (let i = 1; i < values.length; i++) {
		const current = values[i][1];
		const before = values[i - 1][1];
		const delta = current - before;

		// If delta is negative, use current value instead (counter may have reset)
		result.push([values[i][0], delta >= 0 ? delta : current]);
	}

	return filterByStartDate(result, startDate);
};

/**
 * Creates a metric configuration object for Kairos API
 *
 * @param {string} deviceName - Name of the device
 * @param {Object} attribute - Attribute configuration {tag, aggregator}
 * @param {string} breakdown - Time breakdown unit ('minutes' or 'hours')
 * @returns {Object} Metric configuration object
 */
export const createMetric = (deviceName, attribute, breakdown) => ({
	name: deviceName,
	group_by: [
		{
			name: 'tag',
			tags: ['attr'],
		},
	],
	tags: {
		attr: [attribute.tag],
	},
	aggregators: [
		{
			name: attribute.aggregator,
			sampling: {
				value: 1,
				unit: breakdown,
			},
		},
	],
});

/**
 * Builds metrics array for all devices and attributes
 *
 * @param {Array} devices - Array of device names
 * @param {Array} attributeConfigs - Array of attribute configurations
 * @param {string} breakdown - Time breakdown ('minutes', 'hours', etc.)
 * @returns {Array} Array of metric configurations
 */
export const buildMetrics = (devices, attributeConfigs, breakdown) => {
	const normalizedBreakdown = breakdown !== 'minutes' ? 'hours' : breakdown;

	return devices.flatMap((device) => attributeConfigs.map((attr) => createMetric(device, attr, normalizedBreakdown)));
};
